import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import type { User } from '@vigie/shared';
import { ApiError, api } from './api.js';
import { Login } from './pages/Login.js';
import { Dashboard } from './pages/Dashboard.js';
import { Inbox } from './pages/Inbox.js';
import { HitDetail } from './pages/HitDetail.js';
import { Config } from './pages/Config.js';
import { Channels } from './pages/Channels.js';
import { Competitors } from './pages/Competitors.js';
import { Studio } from './pages/Studio.js';
import { ManualAdd } from './pages/ManualAdd.js';

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api
      .me()
      .then((result) => setUser(result.user))
      .catch(() => setUser(null))
      .finally(() => setReady(true));
  }, []);

  if (!ready) {
    return <div className="login-page"><span className="muted">Chargement…</span></div>;
  }
  if (!user) return <Login onAuthenticated={setUser} />;
  return <Shell user={user} onSignedOut={() => setUser(null)} />;
}

function Shell({ user, onSignedOut }: { user: User; onSignedOut: () => void }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<number | null>(null);

  /** Nombre de hits non traités : c'est l'indicateur qui appelle à l'action. */
  const refreshPending = useCallback(() => {
    api
      .hits({ status: 'new', limit: 100 })
      .then((result) => setPending(result.hits.length))
      .catch(() => setPending(null));
  }, []);

  useEffect(refreshPending, [refreshPending]);

  // Une session expirée en cours d'usage ramène proprement au login.
  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof ApiError && event.reason.status === 401) onSignedOut();
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, [onSignedOut]);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    onSignedOut();
    navigate('/');
  };

  return (
    <div className="layout">
      <nav className="sidebar">
        <div className="brand">
          <strong>Vigie</strong>
          <span>veille MCS</span>
        </div>
        <NavLink to="/" end className="nav-link">Tableau de bord</NavLink>
        <NavLink to="/inbox" className="nav-link">
          Inbox
          {pending !== null && pending > 0 ? <span className="count">{pending}</span> : null}
        </NavLink>
        <NavLink to="/ajout" className="nav-link">Ajout manuel</NavLink>
        <NavLink to="/concurrence" className="nav-link">Concurrence</NavLink>
        <NavLink to="/studio" className="nav-link">Studio social</NavLink>
        <NavLink to="/configuration" className="nav-link">Configuration</NavLink>
        <NavLink to="/alertes" className="nav-link">Canaux d’alerte</NavLink>

        <div className="sidebar-footer">
          <div className="email">{user.email}</div>
          <button type="button" className="ghost" onClick={signOut}>Se déconnecter</button>
        </div>
      </nav>

      <main className="main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inbox" element={<Inbox onChanged={refreshPending} />} />
          <Route path="/hits/:hitId" element={<HitDetail onChanged={refreshPending} />} />
          <Route path="/ajout" element={<ManualAdd />} />
          <Route path="/concurrence" element={<Competitors />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/configuration" element={<Config />} />
          <Route path="/alertes" element={<Channels />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
